import { useEffect, useMemo, useState } from 'react'
import { Ban, Clock, ShieldCheck, Eye, EyeOff, KeyRound, RotateCcw, Search, UserPlus, UserRound } from 'lucide-react'
import api, { ehAdministrador } from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'
import ArvoreOpcoesUsuario from '../components/ArvoreOpcoesUsuario'

// Cadastro de Usuários — equivalente à TSil004 do ERP (frota.usucad). Inclui usuário, troca senha e grava a aba Opções
// (só master/super); Geral e Estoque ainda só consulta. A senha (usu_cod) não vem do servidor.
// Desativado só no web (frota.usu_web_inativo); o ERP não é afetado
interface Inativo { motivo: string | null; desativado_por: string | null; desativado_em: string }
interface UsuarioLista {
  usu_nom: string; usu_emp: number; usu_ide: string; usu_nivel: string; usu_tiplib: string; usu_super: string | null
  dep_des: string | null; usu_dua: string | null
  inativo: Inativo | null
}
interface UsuarioDetalhe {
  usu_nom: string; usu_emp: number | null; emp_des: string | null
  usu_funcod: string; fun_nom: string | null; usu_ide: string
  usu_aceemp: string; usu_acedat: string; usu_nivel: string; usu_tiplib: string; usu_super: string | null
  usu_ageati: string; usu_aceopc: string; usu_dep: number | null; dep_des: string | null
  usu_loc: string; usu_colab: string; usu_grasel: string; usu_dua: string | null
  usu_stqent: string; usu_stqsai: string; usu_stqaju: string; usu_stqtra: string
  locais_nao_permitidos: { oed_cod: string; oed_des: string | null; oed_tip: number | null }[]
  inativo: Inativo | null
}

type Aba = 'geral' | 'estoque' | 'opcoes'
const ABAS: { id: Aba; rotulo: string }[] = [
  { id: 'geral', rotulo: 'Geral' }, { id: 'estoque', rotulo: 'Estoque' }, { id: 'opcoes', rotulo: 'Opções' },
]
// TSIL009 — movimentações de estoque permitidas ao usuário
const MOV_ESTOQUE: { campo: 'usu_stqent' | 'usu_stqsai' | 'usu_stqaju' | 'usu_stqtra'; rotulo: string }[] = [
  { campo: 'usu_stqent', rotulo: 'Entrada' }, { campo: 'usu_stqsai', rotulo: 'Saída' },
  { campo: 'usu_stqaju', rotulo: 'Ajuste' }, { campo: 'usu_stqtra', rotulo: 'Transferência' },
]

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px', fontSize: '0.85rem', height: 32,
  background: '#f9fafb', color: '#111827', width: '100%', boxSizing: 'border-box',
}
const desc: React.CSSProperties = { ...inp, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e3a8a', fontWeight: 600 }
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', minHeight: 16 }
const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
}

function Campo({ rotulo, width, children }: { rotulo: string; width: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

function Titulo({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1b3f7b', textTransform: 'uppercase', margin: '0 0 0.6rem' }}>{children}</div>
}

// Somente leitura: cinza sem borda, pra não parecer editável.
const ro: React.CSSProperties = { ...inp, background: '#f3f4f6', borderColor: '#f3f4f6', color: '#374151' }

// Fora da tela; usuário novo grava: aceemp/acedat/aceopc/ageati/grasel='S', nivel=8, tiplib=7.
// usu_loc (almox. O.S.) e usu_colab ficaram fora a pedido.

function iniciais(nome: string) {
  const p = nome.trim().split(/\s+/)
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1][0] : (p[0]?.[1] ?? ''))).toUpperCase()
}

function fmtDataHora(v: string | null) {
  if (!v || v.startsWith('0001') || v < '1901') return ''
  const [d, h] = v.split(' ')
  return `${d.split('-').reverse().join('/')} ${h || ''}`.trim()
}

// Lista: dd/mm/aa hh:mm
function fmtCurto(v: string | null) {
  if (!v || v.startsWith('0001') || v < '1901') return ''
  const [d, h] = v.split(' ')
  const [a, m, dia] = d.split('-')
  return `${dia}/${m}/${a.slice(2)} ${(h || '').slice(0, 5)}`.trim()
}

export default function CadastroUsuarios() {
  const [lista, setLista] = useState<UsuarioLista[]>([])
  const [filtro, setFiltro] = useState('')
  const [selecionado, setSelecionado] = useState<UsuarioDetalhe | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState('')
  const [senha, setSenha] = useState<string | null>(null)
  const admin = ehAdministrador() // senha, desativar e permissões: só master/super
  const [aba, setAba] = useState<Aba>('geral')
  const [empresas, setEmpresas] = useState<{ empcod: number; empnom: string; empfan: string | null }[]>([])

  const [modalDesativar, setModalDesativar] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [processando, setProcessando] = useState(false)
  const [msgSituacao, setMsgSituacao] = useState('')
  const [modalSenha, setModalSenha] = useState(false)
  const [novaSenha, setNovaSenha] = useState('')
  const [confSenha, setConfSenha] = useState('')
  const [erroSenha, setErroSenha] = useState('')
  const { empresaCod } = useEmpresa()
  const [modalNovo, setModalNovo] = useState(false)
  const [novo, setNovo] = useState({ nome: '', senha: '', confirmacao: '', empresa: '' })
  const [erroNovo, setErroNovo] = useState('')

  const carregarLista = () => api.get('/erp/usuarios')
    .then(r => setLista(r.data))
    .catch(e => setErro(e?.response?.data?.error || 'Erro ao carregar os usuários.'))
    .finally(() => setCarregando(false))

  useEffect(() => {
    carregarLista()
    api.get('/erp/empresas').then(r => setEmpresas(r.data)).catch(() => {})
  }, [])

  // Desativar (pede motivo) / reativar no web; recarrega a ficha e a lista
  async function mudarSituacao(acao: 'desativar' | 'reativar') {
    if (!selecionado) return
    if (acao === 'desativar' && !motivo.trim()) { setErro('Informe o motivo da desativação.'); return }
    setProcessando(true); setErro(''); setMsgSituacao('')
    try {
      await api.post(`/erp/usuarios/${encodeURIComponent(selecionado.usu_nom)}/${acao}`, acao === 'desativar' ? { motivo: motivo.trim() } : {})
      setModalDesativar(false); setMotivo('')
      setMsgSituacao(acao === 'desativar' ? `Usuário ${selecionado.usu_nom} desativado no web.` : `Usuário ${selecionado.usu_nom} reativado.`)
      await abrir(selecionado.usu_nom)
      await carregarLista()
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao alterar a situação do usuário.')
    } finally { setProcessando(false) }
  }

  // Troca de senha: mesmas regras da WSil005A do ERP (mín. 4 dígitos, confirmação, não pode já existir)
  function abrirTrocaSenha() {
    setNovaSenha(''); setConfSenha(''); setErroSenha(''); setModalSenha(true)
  }
  async function trocarSenha() {
    if (!selecionado) return
    if (novaSenha.trim().length < 4) { setErroSenha('A nova senha deve ter pelo menos 4 dígitos.'); return }
    if (novaSenha.trim().toUpperCase() !== confSenha.trim().toUpperCase()) { setErroSenha('A confirmação não corresponde à nova senha.'); return }
    setProcessando(true); setErroSenha(''); setMsgSituacao('')
    try {
      await api.put(`/erp/usuarios/${encodeURIComponent(selecionado.usu_nom)}/senha`, { novaSenha: novaSenha.trim(), confirmacao: confSenha.trim() })
      setModalSenha(false); setSenha(null)
      setMsgSituacao(`Senha do usuário ${selecionado.usu_nom} alterada.`)
    } catch (e: any) {
      setErroSenha(e?.response?.data?.error || 'Erro ao trocar a senha.')
    } finally { setProcessando(false) }
  }

  // Incluir usuário (só master/super); depois de gravar abre a ficha dele
  function abrirNovo() {
    setNovo({ nome: '', senha: '', confirmacao: '', empresa: String(empresaCod ?? 1) }); setErroNovo(''); setModalNovo(true)
  }
  async function incluirUsuario() {
    if (!novo.nome.trim()) { setErroNovo('Informe o nome do usuário.'); return }
    if (novo.senha.trim().length < 4) { setErroNovo('A senha deve ter pelo menos 4 dígitos.'); return }
    if (novo.senha.trim().toUpperCase() !== novo.confirmacao.trim().toUpperCase()) { setErroNovo('A confirmação não corresponde à senha.'); return }
    setProcessando(true); setErroNovo(''); setMsgSituacao('')
    try {
      const r = await api.post('/erp/usuarios', { ...novo, empresa: Number(novo.empresa) })
      setModalNovo(false)
      await carregarLista()
      await abrir(r.data.nome)
      setMsgSituacao(`Usuário ${r.data.nome} incluído. Confira as telas liberadas na aba Opções.`)
    } catch (e: any) {
      setErroNovo(e?.response?.data?.error || 'Erro ao incluir o usuário.')
    } finally { setProcessando(false) }
  }

  // Padrão mostra só os ativos; "Desativados" lista os bloqueados no web
  const [verInativos, setVerInativos] = useState(false)
  const daSituacao = useMemo(() => lista.filter(u => !!u.inativo === verInativos), [lista, verInativos])
  const qtdInativos = useMemo(() => lista.filter(u => u.inativo).length, [lista])
  const filtrados = useMemo(() => {
    const f = filtro.trim().toUpperCase()
    return f ? daSituacao.filter(u => u.usu_nom.toUpperCase().includes(f) || (u.usu_ide || '').toUpperCase().includes(f)) : daSituacao
  }, [daSituacao, filtro])

  async function abrir(nome: string) {
    setErro('')
    setSenha(null)
    if (nome !== selecionado?.usu_nom) setMsgSituacao('')
    try {
      const r = await api.get(`/erp/usuarios/${encodeURIComponent(nome)}`)
      setSelecionado(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao abrir o usuário.')
    }
  }

  async function alternarSenha() {
    if (senha !== null || !u) { setSenha(null); return }
    try {
      const r = await api.get(`/erp/usuarios/${encodeURIComponent(u.usu_nom)}/senha`)
      setSenha(r.data.senha ?? '')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar a senha.')
    }
  }

  // Ao trocar o código, o nome da empresa acompanha (cademp.empfan).
  function trocarEmpresa(valor: string) {
    if (!u) return
    const cod = valor.replace(/\D/g, '')
    const emp = cod ? empresas.find(x => Number(x.empcod) === Number(cod)) : undefined
    setSelecionado({ ...u, usu_emp: cod ? Number(cod) : null, emp_des: emp ? (emp.empfan || emp.empnom) : null })
  }

  const u = selecionado

  return (
    <div>
      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {modalNovo && (
        <div className="modal-overlay" onClick={() => !processando && setModalNovo(false)}>
          <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Incluir usuário</h2>
              <button className="modal-fechar" onClick={() => setModalNovo(false)} disabled={processando}>✕</button>
            </div>
            <div className="modal-corpo">
              <p style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '0.7rem' }}>
                Vale para o FrotaWeb e para o ERP. Senha de 4 a 6 caracteres.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                <Campo rotulo="Nome *" width={140}>
                  <input style={{ ...inp, background: '#fff' }} maxLength={10} autoFocus autoComplete="off"
                    value={novo.nome} onChange={e => setNovo({ ...novo, nome: e.target.value })} />
                </Campo>
                <div style={{ display: 'flex', gap: '0.7rem' }}>
                  <Campo rotulo="Senha *" width={110}>
                    <input style={{ ...inp, background: '#fff' }} type="password" maxLength={6} autoComplete="new-password"
                      value={novo.senha} onChange={e => setNovo({ ...novo, senha: e.target.value })} />
                  </Campo>
                  <Campo rotulo="Confirmação *" width={110}>
                    <input style={{ ...inp, background: '#fff' }} type="password" maxLength={6} autoComplete="new-password"
                      value={novo.confirmacao} onChange={e => setNovo({ ...novo, confirmacao: e.target.value })} />
                  </Campo>
                </div>
                <Campo rotulo="Estabelecimento *" width={300}>
                  <select style={{ ...inp, background: '#fff' }} value={novo.empresa} onChange={e => setNovo({ ...novo, empresa: e.target.value })}>
                    {empresas.map(x => <option key={x.empcod} value={x.empcod}>{x.empcod} - {x.empfan || x.empnom}</option>)}
                  </select>
                </Campo>
              </div>
              {erroNovo && <div style={{ marginTop: '0.7rem', fontSize: '0.8rem', fontWeight: 600, color: '#b91c1c' }}>{erroNovo}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '0.9rem' }}>
                <button className="btn btn-secondary" onClick={() => setModalNovo(false)} disabled={processando}>Cancelar</button>
                <button className="btn btn-primary" onClick={incluirUsuario} disabled={processando || !novo.nome.trim() || !novo.senha || !novo.confirmacao}>
                  {processando ? 'Incluindo...' : 'Incluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Lista */}
        <div style={{ ...card, width: 320, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '0.85rem 0.9rem 0.7rem', borderBottom: '1px solid #eef2f7' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.55rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1b3f7b', textTransform: 'uppercase' }}>Usuários</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.72rem', color: '#94a3b8' }}>
                {filtro.trim() ? `${filtrados.length} de ${daSituacao.length}` : daSituacao.length}
                {admin && (
                  <button type="button" className="btn btn-primary" onClick={abrirNovo}
                    style={{ height: 26, padding: '0 9px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <UserPlus size={13} /> Incluir
                  </button>
                )}
              </span>
            </div>
            <div style={{ position: 'relative' }}>
              <Search size={15} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input style={{ ...inp, background: '#fff', paddingLeft: 30 }} placeholder="Buscar por nome ou identificação"
                value={filtro} onChange={e => setFiltro(e.target.value)} />
            </div>
            <div className="usu-filtro">
              <button type="button" className={!verInativos ? 'ativo' : ''} onClick={() => setVerInativos(false)}>Ativos</button>
              <button type="button" className={verInativos ? 'ativo' : ''} onClick={() => setVerInativos(true)}>
                Desativados{qtdInativos > 0 && <span>{qtdInativos}</span>}
              </button>
            </div>
          </div>
          <div style={{ maxHeight: 540, overflowY: 'auto', padding: '0.35rem' }}>
            {carregando && <div style={{ color: '#6b7280', fontSize: '0.85rem', padding: '0.75rem' }}>Carregando...</div>}
            {!carregando && filtrados.length === 0 && <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.75rem', textAlign: 'center' }}>Nenhum usuário encontrado</div>}
            {filtrados.map(x => {
              const ativo = u?.usu_nom === x.usu_nom
              const master = x.usu_super === 'S'
              return (
                <button key={x.usu_nom} onClick={() => abrir(x.usu_nom)} className="usu-item"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                    borderRadius: 6, padding: '0.45rem 0.55rem', marginBottom: 1,
                    background: ativo ? '#eff6ff' : undefined, boxShadow: ativo ? 'inset 3px 0 0 #1b3f7b' : undefined,
                  }}>
                  <span style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', fontWeight: 700, background: master ? '#f5c518' : ativo ? '#1b3f7b' : '#e2e8f0', color: master ? '#5c4300' : ativo ? '#fff' : '#475569',
                    boxShadow: master ? '0 0 0 2px #fff, 0 0 0 3.5px #e0a800' : undefined,
                  }}>{iniciais(x.usu_nom)}</span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', color: x.inativo ? '#9ca3af' : '#111827', textDecoration: x.inativo ? 'line-through' : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.usu_nom}</span>
                    <span style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.dep_des || 'Sem departamento'}</span>
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                  {master && !x.inativo
                    ? <span title="Super usuário: acesso total e cria usuários" style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.64rem', fontWeight: 800, color: '#8a6100', background: '#fff4cc', border: '1px solid #f5d76e', borderRadius: 10, padding: '1px 7px' }}><ShieldCheck size={11} /> Master</span>
                    : x.inativo
                    ? <span title={x.inativo.motivo || 'Desativado'} style={{ fontSize: '0.64rem', fontWeight: 800, color: '#c62e3d', background: '#ffe3e6', borderRadius: 10, padding: '1px 7px' }}>Inativo</span>
                    : x.usu_ide && <span style={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace', background: '#f1f5f9', borderRadius: 4, padding: '1px 5px' }}>{x.usu_ide}</span>}
                  <span title="Último acesso" style={{ fontSize: '0.66rem', fontWeight: 600, color: fmtCurto(x.usu_dua) ? '#2563eb' : '#dc2626', whiteSpace: 'nowrap' }}>{fmtCurto(x.usu_dua) || 'Nunca acessou'}</span>
                  </span>
                </button>
              )
            })}
          </div>
          <style>{`.usu-item{background:transparent}.usu-item:hover{background:#f8fafc}
            .usu-filtro{display:flex;gap:2px;margin-top:0.55rem;background:#f1f5f9;border-radius:6px;padding:2px}
            .usu-filtro button{flex:1;border:none;background:transparent;border-radius:5px;padding:4px 0;font-family:inherit;font-size:0.76rem;font-weight:600;color:#64748b;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:5px}
            .usu-filtro button.ativo{background:#fff;color:#1b3f7b;box-shadow:0 1px 2px rgba(0,0,0,.08)}
            .usu-filtro button span{font-size:0.66rem;font-weight:800;color:#c62e3d;background:#ffe3e6;border-radius:9px;padding:0 6px}
            .usu-situacao{display:inline-flex;align-items:center;gap:5px;font-family:inherit;-webkit-font-smoothing:antialiased;border:1px solid #f1aeb5;background:#fff5f6;color:#c62e3d;border-radius:6px;padding:3px 10px;font-size:0.78rem;font-weight:600;line-height:1.3;cursor:pointer;transition:all .15s}
            .usu-situacao:hover{border-color:#db3545;background:#ffe3e6;color:#a8202f}
            .usu-situacao--reativar{border-color:#a7dcbf;background:#f0faf4;color:#16884a}
            .usu-situacao--reativar:hover{border-color:#16884a;background:#dff3e8;color:#0f6b39}
            .usu-situacao:disabled{opacity:.6;cursor:wait}`}</style>
        </div>

        {/* Ficha */}
        <div style={{ flex: 1, minWidth: 520, maxWidth: aba === 'opcoes' ? 1150 : 760, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {!u ? (
            <div style={{ ...card, color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: '2.5rem 1rem' }}>
              <UserRound size={28} style={{ color: '#cbd5e1', marginBottom: 6 }} />
              <div>Selecione um usuário na lista.</div>
            </div>
          ) : (
            <>
              {/* Cabeçalho: quem é + último acesso (informativo, não é campo) */}
              <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: u.usu_super === 'S' ? '#f5c518' : '#1b3f7b', color: u.usu_super === 'S' ? '#5c4300' : '#fff',
                  boxShadow: u.usu_super === 'S' ? '0 0 0 2px #fff, 0 0 0 4px #e0a800' : undefined,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1rem',
                }}>{iniciais(u.usu_nom)}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
                    {u.usu_nom}
                    {u.usu_super === 'S' && (
                      <span title="Super usuário: acesso total a todas as telas e é quem cria usuários e troca senhas"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', fontWeight: 800, color: '#8a6100', background: '#fff4cc', border: '1px solid #f5d76e', borderRadius: 10, padding: '2px 9px' }}>
                        <ShieldCheck size={12} /> Usuário master
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                    {[u.dep_des || 'Sem departamento', u.emp_des].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right', fontSize: '0.75rem', color: '#6b7280' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                    <Clock size={13} /> Último acesso
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>{fmtDataHora(u.usu_dua) || 'Nunca acessou'}</div>
                </div>
                {/* Situação no web: etiqueta + ação discreta (só fica colorida no hover) */}
                <div style={{ borderLeft: '1px solid #eef2f7', paddingLeft: '0.9rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                  <span style={{
                    fontSize: '0.7rem', fontWeight: 700, borderRadius: 10, padding: '1px 8px',
                    color: u.inativo ? '#c62e3d' : '#16884a', background: u.inativo ? '#ffe3e6' : '#e7f6ee',
                  }}>● {u.inativo ? 'Inativo' : 'Ativo'}</span>
                  {!admin ? null : u.inativo ? (
                    <button type="button" className="usu-situacao usu-situacao--reativar" onClick={() => mudarSituacao('reativar')} disabled={processando} title="Reativar o acesso ao web">
                      <RotateCcw size={13} /> Reativar
                    </button>
                  ) : (
                    <button type="button" className="usu-situacao" onClick={() => { setMotivo(''); setModalDesativar(true) }} disabled={processando} title="Desativar o acesso ao web">
                      <Ban size={13} /> Desativar
                    </button>
                  )}
                </div>
              </div>

              {msgSituacao && <div style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.5rem 0.85rem', borderRadius: 8, background: '#d1fae5', color: '#065f46' }}>{msgSituacao}</div>}
              {u.inativo && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '0.7rem 0.9rem', borderRadius: 8, background: '#fff3f4', border: '1px solid #ffd4da', borderLeft: '4px solid #db3545', fontSize: '0.8rem', color: '#7f1d1d' }}>
                  <Ban size={18} color="#db3545" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <b>Usuário desativado no web</b> em {u.inativo.desativado_em}{u.inativo.desativado_por ? ` por ${u.inativo.desativado_por}` : ''}.
                    {u.inativo.motivo && <div>Motivo: {u.inativo.motivo}</div>}
                    <div style={{ fontSize: '0.72rem', color: '#9f3a46', marginTop: 2 }}>Não consegue entrar no FrotaWeb. O acesso ao ERP não é afetado.</div>
                  </div>
                </div>
              )}

              {modalDesativar && (
                <div className="modal-overlay" onClick={() => !processando && setModalDesativar(false)}>
                  <div className="modal-box" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h2>Desativar {u.usu_nom}</h2>
                      <button className="modal-fechar" onClick={() => setModalDesativar(false)} disabled={processando}>✕</button>
                    </div>
                    <div className="modal-corpo">
                      <p style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '0.7rem' }}>
                        O usuário não vai conseguir entrar no FrotaWeb e, se estiver logado, sai na próxima ação. O acesso ao ERP continua igual.
                      </p>
                      <label style={label}>Motivo *</label>
                      <textarea rows={3} maxLength={200} value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus
                        placeholder="Ex.: desligado da empresa, férias, troca de função..."
                        style={{ ...inp, background: '#fff', height: 'auto', resize: 'vertical', marginTop: 3 }} />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '0.9rem' }}>
                        <button className="btn btn-secondary" onClick={() => setModalDesativar(false)} disabled={processando}>Cancelar</button>
                        <button className="btn btn-danger-solid" onClick={() => mudarSituacao('desativar')} disabled={processando || !motivo.trim()}>
                          {processando ? 'Desativando...' : 'Desativar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e2e8f0', marginBottom: '-0.25rem' }}>
                {ABAS.map(a => (
                  <button key={a.id} type="button" onClick={() => setAba(a.id)}
                    style={{
                      border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.5rem 1rem', fontSize: '0.85rem',
                      fontWeight: aba === a.id ? 700 : 500, color: aba === a.id ? '#1b3f7b' : '#64748b',
                      borderBottom: aba === a.id ? '2px solid #1b3f7b' : '2px solid transparent', marginBottom: -1,
                    }}>{a.rotulo}</button>
                ))}
              </div>

              {aba === 'opcoes' && (
                <div style={card}>
                  <Titulo>Permissões nas telas do web</Titulo>
                  <ArvoreOpcoesUsuario nome={u.usu_nom} tiplib={u.usu_tiplib} usuarios={lista.map(x => x.usu_nom)} />
                </div>
              )}

              {aba === 'estoque' && (
                <>
                  <div style={card}>
                    <Titulo>Movimentações permitidas</Titulo>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 130px)', gap: '0.8rem 1rem' }}>
                      {MOV_ESTOQUE.map(m => (
                        <Campo key={m.campo} rotulo={m.rotulo} width={130}>
                          <select style={{ ...inp, background: '#fff' }} value={u[m.campo] || ''}
                            onChange={e => setSelecionado({ ...u, [m.campo]: e.target.value })}>
                            {!u[m.campo] && <option value="">Não informado</option>}
                            <option value="S">Sim</option>
                            <option value="N">Não</option>
                          </select>
                        </Campo>
                      ))}
                    </div>
                  </div>

                  <div style={card}>
                    <Titulo>Locais não permitidos</Titulo>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '0.45rem 0.6rem', width: 110 }}>Estocagem</th>
                          <th style={{ textAlign: 'left', padding: '0.45rem 0.6rem' }}>Descrição</th>
                          <th style={{ textAlign: 'right', padding: '0.45rem 0.6rem', width: 70 }}>Tipo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.locais_nao_permitidos.length === 0 ? (
                          <tr><td colSpan={3} style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>
                            Nenhum local bloqueado — o usuário movimenta em todos.
                          </td></tr>
                        ) : u.locais_nao_permitidos.map(l => (
                          <tr key={l.oed_cod} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '0.45rem 0.6rem', fontFamily: 'monospace' }}>{l.oed_cod}</td>
                            <td style={{ padding: '0.45rem 0.6rem' }}>{l.oed_des || <span style={{ color: '#dc2626' }}>Local não cadastrado</span>}</td>
                            <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right' }}>{l.oed_tip ?? ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {aba === 'geral' && (<>
              <div style={card}>
                <Titulo>Acesso</Titulo>
                <div style={{ display: 'flex', gap: '0.8rem 1rem', flexWrap: 'wrap' }}>
                  <Campo rotulo="Nome" width={200}>
                    <input style={ro} value={u.usu_nom} readOnly />
                  </Campo>
                  <Campo rotulo="Senha" width={160}>
                    <div style={{ position: 'relative' }}>
                      <input style={{ ...ro, paddingRight: 30 }} type={senha !== null ? 'text' : 'password'}
                        value={senha ?? '******'} readOnly />
                      {admin && <button type="button" onClick={alternarSenha} title={senha !== null ? 'Ocultar senha' : 'Mostrar senha'}
                        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none',
                          background: 'transparent', cursor: 'pointer', color: '#6b7280', padding: 0, display: 'flex' }}>
                        {senha !== null ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>}
                    </div>
                  </Campo>
                  {admin && <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button type="button" className="btn btn-secondary" onClick={abrirTrocaSenha} disabled={processando}
                      style={{ height: 32, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                      <KeyRound size={14} /> Trocar senha
                    </button>
                  </div>}
                </div>
              </div>

              {modalSenha && (
                <div className="modal-overlay" onClick={() => !processando && setModalSenha(false)}>
                  <div className="modal-box" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h2>Trocar senha de {u.usu_nom}</h2>
                      <button className="modal-fechar" onClick={() => setModalSenha(false)} disabled={processando}>✕</button>
                    </div>
                    <div className="modal-corpo">
                      <p style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '0.7rem' }}>
                        Vale para o FrotaWeb e para o ERP. De 4 a 6 caracteres.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                        <Campo rotulo="Nova senha *" width={180}>
                          <input style={{ ...inp, background: '#fff' }} type="password" maxLength={6} autoFocus autoComplete="new-password"
                            value={novaSenha} onChange={e => setNovaSenha(e.target.value)} />
                        </Campo>
                        <Campo rotulo="Confirmação da nova senha *" width={180}>
                          <input style={{ ...inp, background: '#fff' }} type="password" maxLength={6} autoComplete="new-password"
                            value={confSenha} onChange={e => setConfSenha(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') trocarSenha() }} />
                        </Campo>
                      </div>
                      {erroSenha && <div style={{ marginTop: '0.7rem', fontSize: '0.8rem', fontWeight: 600, color: '#b91c1c' }}>{erroSenha}</div>}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '0.9rem' }}>
                        <button className="btn btn-secondary" onClick={() => setModalSenha(false)} disabled={processando}>Cancelar</button>
                        <button className="btn btn-primary" onClick={trocarSenha} disabled={processando || !novaSenha || !confSenha}>
                          {processando ? 'Alterando...' : 'Trocar senha'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={card}>
                <Titulo>Vínculos</Titulo>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  <Campo rotulo="Estabelecimento" width={460}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input style={{ ...inp, background: '#fff', width: 70, flexShrink: 0 }} inputMode="numeric" maxLength={4}
                        value={u.usu_emp ?? ''} onChange={e => trocarEmpresa(e.target.value)} />
                      <input style={desc} value={u.emp_des || ''} readOnly tabIndex={-1} />
                    </div>
                  </Campo>
                  <Campo rotulo="Funcionário" width={460}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input style={{ ...ro, width: 70, flexShrink: 0 }} value={u.usu_funcod} readOnly tabIndex={-1} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <LookupSelect endpoint="/combustivel/motoristas" codKey="motcod" descKey="motnome" tagKey="motcod"
                          codValue={u.usu_funcod || ''} descValue={u.fun_nom || ''} placeholder="Pesquisar funcionário..." listWidth={380}
                          onSelect={(cod, nome) => setSelecionado({ ...u, usu_funcod: cod, fun_nom: nome || null })} />
                      </div>
                    </div>
                  </Campo>
                  <Campo rotulo="Departamento" width={460}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input style={{ ...ro, width: 70, flexShrink: 0 }} value={u.usu_dep ?? ''} readOnly tabIndex={-1} />
                      <input style={desc} value={u.dep_des || ''} readOnly tabIndex={-1} />
                    </div>
                  </Campo>
                </div>
              </div>
              </>)}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
